// Export dialog (docs/07 §7.7) — choose loop count + tail fade, show progress,
// then offer the downloaded file's name/size.

import { createDialog, type Dialog } from "./dialog.ts";
import type { ExportFormat } from "../share/export.ts";

export interface ExportRequest {
  loops: number;
  fade: boolean;
  format: ExportFormat;
  stems: boolean;
}

export interface ExportDialog {
  element: HTMLElement;
  open(): void;
  setProgress(p: number): void;
  setResult(filename: string, bytes: number): void;
}

export function createExportDialog(onExport: (req: ExportRequest) => void): ExportDialog {
  const dialog: Dialog = createDialog("Export mix");

  const loopsLabel = document.createElement("label");
  loopsLabel.className = "field";
  loopsLabel.textContent = "Loops to capture";
  const loops = document.createElement("input");
  loops.type = "number";
  loops.min = "1";
  loops.max = "32";
  loops.value = "4";
  loops.className = "field__input";
  loopsLabel.append(loops);

  // Format: WAV (lossless) or MP3 (smaller)
  const fmtField = document.createElement("div");
  fmtField.className = "field";
  const fmtCaption = document.createElement("span");
  fmtCaption.textContent = "Format";
  const seg = document.createElement("div");
  seg.className = "seg";
  let format: ExportFormat = "wav";
  for (const f of ["wav", "mp3"] as const) {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "seg__btn";
    b.textContent = f.toUpperCase();
    b.setAttribute("aria-pressed", String(f === format));
    if (f === format) b.classList.add("is-active");
    b.addEventListener("click", () => {
      format = f;
      for (const x of seg.children) {
        x.classList.toggle("is-active", x === b);
        x.setAttribute("aria-pressed", String(x === b));
      }
    });
    seg.append(b);
  }
  fmtField.append(fmtCaption, seg);

  const fadeLabel = document.createElement("label");
  fadeLabel.className = "field field--check";
  const fade = document.createElement("input");
  fade.type = "checkbox";
  fade.checked = true;
  fadeLabel.append(fade, document.createTextNode(" Fade out the tail"));

  const stemsLabel = document.createElement("label");
  stemsLabel.className = "field field--check";
  const stems = document.createElement("input");
  stems.type = "checkbox";
  stemsLabel.append(stems, document.createTextNode(" Export stems (one file per layer)"));

  const go = document.createElement("button");
  go.type = "button";
  go.className = "btn";
  go.textContent = "Export";

  const progress = document.createElement("div");
  progress.className = "export-progress";
  progress.hidden = true;
  const bar = document.createElement("div");
  bar.className = "export-progress__bar";
  const status = document.createElement("p");
  status.className = "export-status";
  progress.append(bar);

  go.addEventListener("click", () => {
    progress.hidden = false;
    bar.style.width = "0%";
    status.textContent = stems.checked ? "Rendering stems…" : "Recording mix…";
    go.disabled = true;
    onExport({
      loops: Math.max(1, Math.min(32, Number(loops.value) || 4)),
      fade: fade.checked,
      format,
      stems: stems.checked,
    });
  });

  dialog.body.append(loopsLabel, fmtField, fadeLabel, stemsLabel, go, progress, status);

  return {
    element: dialog.element,
    open() {
      go.disabled = false;
      progress.hidden = true;
      status.textContent = "";
      dialog.open();
    },
    setProgress(p) {
      bar.style.width = `${Math.round(Math.max(0, Math.min(1, p)) * 100)}%`;
    },
    setResult(filename, bytes) {
      go.disabled = false;
      status.textContent = bytes > 0 ? `Saved ${filename} (${(bytes / 1024).toFixed(0)} KB)` : filename;
    },
  };
}
